import React, { useState, useEffect, useRef, useCallback } from "react";
import { SnackbarProvider, enqueueSnackbar } from "notistack";
import {
  Radio,
  RadioGroup,
  Stack,
  Typography,
  Box,
  FormControlLabel,
  Link,
  Button,
} from "@mui/material";
import IsByCorporationTable from "./ByCorporationTable/ByCorporationTable";
import { Dropdown } from "../../HelperFunctions/PayrollDropdown";
import { AUTOMATION_TYPES } from "../../Enums/Enum";
import {
  getPayrollVendorTypes,
  savePayrollPreference,
  getPayrollPreference,
  getPayrollEmail,
} from "../../api";
import PayrollsPopup from "../../Components/PayrollPopup";
import { useAlerts } from "../../Hooks";
import { Loader } from "../../Components/Loader";
import PayrollItemTypeMapping from "../../Components/PayrollItemTypeMapping";
import ApiConfigurationModal, { validateApiCredentials } from "./ApiConfigurationModal";
import { buildPayrollPreferencePayload } from "../../Payloads/PayrollPreferencePayload";

// --- Constants ---
const PREFERENCE_MODES = {
  BY_CORP: "by_corporation",
  ALL_CORP: "all_corporation",
};

const AUTOMATION_OPTIONS = [
  { id: "", label: "--Select--" },
  { id: AUTOMATION_TYPES.EMAIL, label: "Import/EMAIL" },
  { id: AUTOMATION_TYPES.API, label: "API" },
];

const COMPACT_DROPDOWN_SX = {
  "& .MuiFilledInput-root": {
    height: "28px",
    minHeight: "28px",
    backgroundColor: "white",
    paddingTop: 0,
    paddingBottom: 0,
    "&:hover:not(.Mui-disabled)": { backgroundColor: "white" },
    "&.Mui-focused": { backgroundColor: "white" },
  },
  "& .MuiAutocomplete-input": {
    padding: "0px 8px !important",
    fontSize: "0.7rem",
    height: "28px",
  },
};

/**
 * Payroll Preference Settings Page.
 * Configures hours conversion, mapped items, and vendor automation settings per corporation or globally.
 */
const PayrollPreference = () => {
  // General Preferences State
  const [loading, setLoading] = useState(true);
  const [preferenceId, setPreferenceId] = useState("");
  const [isPayrollHours, setIsPayrollHours] = useState(true);
  const [companyPreference, setCompanyPreference] = useState(PREFERENCE_MODES.BY_CORP);

  // All Corporations Mode State
  const [allCorpDetailId, setAllCorpDetailId] = useState("");
  const [importGroup1, setImportGroup1] = useState(""); // Automation Type
  const [importGroup2, setImportGroup2] = useState(""); // Vendor ID
  const [apiKey, setApiKey] = useState(null);
  const [securityKey, setSecurityKey] = useState(null);
  const [isApiConfigured, setIsApiConfigured] = useState(false);

  // By Corporation Mode State
  const [corpRows, setCorpRows] = useState([
    {
      id: Date.now(),
      detailId: "",
      legalName: "",
      dba: "",
      automationType: "",
      payrollName: "",
      apiOrEmail: "",
      corpId: "",
      apiKey: "",
      securityKey: "",
      apiConfigured: false,
    },
  ]);
  const prevCorpRowsRef = useRef([]);

  // Vendor Types & Item Mapping State
  const [payrollVendorTypes, setPayrollVendorTypes] = useState([]);
  const [isApiModalOpen, setIsApiModalOpen] = useState(false);
  const [isItemMappingOpen, setIsItemMappingOpen] = useState(false);
  const [apiModalData, setApiModalData] = useState({ apiKey: "", securityKey: "" });
  const [activeCorpRowId, setActiveCorpRowId] = useState(null);
  const [itemMapCount, setItemMapCount] = useState(0);

  const { popup: alertPopup, showErrorPopup, hidePopup } = useAlerts();

  // --- Data Fetching Methods ---
  const fetchVendorTypes = useCallback(async () => {
    try {
      const data = await getPayrollVendorTypes();
      const vendors = data?.LoadPayrollVendorTypes || data || [];
      const formattedVendors = Array.isArray(vendors) ? vendors : [];
      setPayrollVendorTypes([{ ID: "", Value: "--Select--" }, ...formattedVendors]);
    } catch (error) {
      console.error("Error fetching payroll vendor types:", error);
    }
  }, []);

  const formatCorpRow = (d, index) => ({
    id: d.ID || Date.now() + index,
    detailId: d.ID || "",
    corpId: d.CorporationID || "",
    legalName: d.CorporationName || "",
    dba: d.CorporationDBName || "",
    automationType: String(d.DataAutomationType ?? ""),
    payrollName: d.PayrollVendorID || "",
    apiOrEmail: d.PayrollEmail || "",
    apiKey: d.APIKey || "",
    securityKey: d.SecurityKey || "",
    apiConfigured: Boolean(d.IsConfigured),
  });

  const populatePreferencesData = useCallback((data) => {
    if (!data || data.length === 0) return;

    if (data[0].PayrollItemCount !== undefined && data[0].PayrollItemCount !== null) {
      setItemMapCount(data[0].PayrollItemCount);
    }

    setIsPayrollHours(data[0].IsPayrollHours ?? true);
    setCompanyPreference(data[0].IsByCorporation ? PREFERENCE_MODES.BY_CORP : PREFERENCE_MODES.ALL_CORP);
    setPreferenceId(data[0].PreferenceID || "");

    const allCorp = data.find((d) => !d.CorporationID);
    const byCorp = data.filter((d) => d.CorporationID);
    const isByCorp = data[0].IsByCorporation;

    if (isByCorp && byCorp.length > 0) {
      setCorpRows(byCorp.map(formatCorpRow));
    } else {
      setCorpRows([
        {
          id: Date.now(),
          detailId: "",
          legalName: "",
          dba: "",
          automationType: "",
          payrollName: "",
          apiOrEmail: "",
          corpId: "",
          apiKey: "",
          securityKey: "",
          apiConfigured: false,
        },
      ]);
    }

    if (!isByCorp && allCorp) {
      setImportGroup1(String(allCorp.DataAutomationType ?? ""));
      setImportGroup2(allCorp.PayrollVendorID || "");
      setAllCorpDetailId(allCorp.ID || "");
      setApiKey(allCorp.APIKey || null);
      setSecurityKey(allCorp.SecurityKey || null);
      setIsApiConfigured(Boolean(allCorp.IsConfigured));
    } else {
      setImportGroup1("");
      setImportGroup2("");
      setAllCorpDetailId(allCorp?.ID || "");
      setApiKey(null);
      setSecurityKey(null);
      setIsApiConfigured(false);
    }
  }, []);

  const fetchPreferences = useCallback(async () => {
    try {
      const response = await getPayrollPreference();
      if (response?.StatusCode === 200 && response.PayrollPreferenceDetailsResponse) {
        populatePreferencesData(response.PayrollPreferenceDetailsResponse);
      }
    } catch (error) {
      console.error("Error fetching payroll preference details:", error);
    }
  }, [populatePreferencesData]);

  // Initial Fetch on Component Mount
  useEffect(() => {
    const initialize = async () => {
      setLoading(true);
      try {
        await Promise.all([fetchVendorTypes(), fetchPreferences()]);
      } catch (error) {
        console.error("Error initializing payroll preferences:", error);
      } finally {
        setLoading(false);
      }
    };
    initialize();
  }, [fetchVendorTypes, fetchPreferences]);

  // --- Automatic Email Fetching when Automation is EMAIL or Corporation Changes ---
  const fetchEmailsForRows = useCallback(async (rows) => {
    const fetchPromises = rows.map(async (row) => {
      try {
        const emailResponse = await getPayrollEmail(row.corpId);
        if (emailResponse?.statusCode === 200 || emailResponse?.StatusCode === 200) {
          return { id: row.id, email: emailResponse.Email || emailResponse.email || "" };
        }
      } catch (error) {
        console.error("Error fetching email:", error);
      }
      enqueueSnackbar(`Could not fetch email for ${row.legalName || "corporation"}.`, { variant: "warning" });
      return { id: row.id, email: "" };
    });

    const results = await Promise.all(fetchPromises);

    setCorpRows((currentRows) =>
      currentRows.map((r) => {
        const result = results.find((res) => res.id === r.id);
        if (result) {
          return { ...r, apiOrEmail: result.email };
        }
        return r;
      })
    );
  }, []);

  useEffect(() => {
    const prevRows = prevCorpRowsRef.current || [];
    prevCorpRowsRef.current = corpRows;

    if (prevRows.length === 0) return;

    const rowsRequiringEmailFetch = corpRows.filter((row) => {
      const prevRow = prevRows.find((p) => p.id === row.id);
      if (!prevRow || !row.corpId) return false;

      const automationChangedToEmail =
        row.automationType === AUTOMATION_TYPES.EMAIL && prevRow.automationType !== AUTOMATION_TYPES.EMAIL;
      const corporationChangedWithEmail =
        row.automationType === AUTOMATION_TYPES.EMAIL && row.corpId !== prevRow.corpId;

      return automationChangedToEmail || corporationChangedWithEmail;
    });

    if (rowsRequiringEmailFetch.length > 0) {
      fetchEmailsForRows(rowsRequiringEmailFetch);
    }
  }, [corpRows, fetchEmailsForRows]);

  // --- API Modal Handlers ---
  const handleOpenApiModalForCorp = (id) => {
    const row = corpRows.find((r) => r.id === id);
    setApiModalData({ apiKey: row?.apiKey || "", securityKey: row?.securityKey || "" });
    setActiveCorpRowId(id);
    setIsApiModalOpen(true);
  };

  const handleOpenApiModalForAll = () => {
    setApiModalData({ apiKey: apiKey || "", securityKey: securityKey || "" });
    setActiveCorpRowId(null);
    setIsApiModalOpen(true);
  };

  const handleApiModalSave = ({ apiKey: newApiKey, securityKey: newSecurityKey }) => {
    if (activeCorpRowId) {
      setCorpRows((prev) =>
        prev.map((row) =>
          row.id === activeCorpRowId
            ? {
                ...row,
                apiKey: newApiKey,
                securityKey: newSecurityKey,
                apiConfigured: true,
              }
            : row
        )
      );
    } else {
      setApiKey(newApiKey);
      setSecurityKey(newSecurityKey);
      setIsApiConfigured(true);
    }
  };

  // --- Save Validation ---
  const validateBeforeSave = () => {
    const isByCorp = companyPreference === PREFERENCE_MODES.BY_CORP;
    let isValid = true;

    if (isByCorp) {
      const activeRows = corpRows.filter((row) => row.corpId || row.automationType || row.payrollName);

      if (activeRows.length === 0) {
        enqueueSnackbar("At least 1 row is required.", { variant: "error" });
        return false;
      }

      const corpIdSet = new Set();
      for (const row of activeRows) {
        if (row.corpId) {
          if (corpIdSet.has(row.corpId)) {
            showErrorPopup("Duplicate corporation selected. Please select a unique corporation for each row.");
            return false;
          }
          corpIdSet.add(row.corpId);
        }
      }

      for (const row of activeRows) {
        if (!row.corpId) {
          enqueueSnackbar("Corporation Legal Name is required for all active rows.", { variant: "error" });
          return false;
        }
        if (!row.automationType) {
          enqueueSnackbar(`Data Automation type is required for corporation "${row.legalName}".`, { variant: "error" });
          return false;
        }
        if (!row.payrollName) {
          enqueueSnackbar(`Payroll Name is required for corporation "${row.legalName}".`, { variant: "error" });
          return false;
        }
      }

      const apiRows = corpRows.filter((row) => row.automationType === AUTOMATION_TYPES.API);
      apiRows.forEach((row) => {
        const errors = validateApiCredentials(row.apiKey, row.securityKey);
        if (errors.length > 0) {
          errors.forEach((err) =>
            enqueueSnackbar(`${err} for ${row.legalName || "Corporation"}`, { variant: "error" })
          );
          isValid = false;
        }
      });
    } else {
      if (importGroup1 === AUTOMATION_TYPES.API) {
        const errors = validateApiCredentials(apiKey, securityKey);
        if (errors.length > 0) {
          errors.forEach((err) => enqueueSnackbar(err, { variant: "error" }));
          isValid = false;
        }
      }
      if (!importGroup1) {
        enqueueSnackbar("Data Automation type is required.", { variant: "error" });
        return false;
      }
      if (!importGroup2) {
        enqueueSnackbar("Payroll Type is required.", { variant: "error" });
        return false;
      }
    }

    return isValid;
  };

  // --- Save Preferences Action ---
  const handleSave = async () => {
    if (!validateBeforeSave()) return;

    const payload = buildPayrollPreferencePayload({
      preferenceId,
      isPayrollHours,
      companyPreference,
      byCorpModeKey: PREFERENCE_MODES.BY_CORP,
      corpRows,
      allCorpDetailId,
      importGroup1,
      importGroup2,
      apiKey,
      securityKey,
    });

    try {
      const response = await savePayrollPreference(payload);
      if (response?.StatusCode === 200 || response?.Status === "Success") {
        enqueueSnackbar(response?.Status || "Payroll preferences saved successfully.", {
          variant: "success",
          anchorOrigin: { vertical: "top", horizontal: "right" },
        });

        setTimeout(async () => {
          await fetchPreferences();
        }, 1000);
      } else {
        enqueueSnackbar(response?.Message || response?.Status || "Failed to save payroll preferences.", {
          variant: "error",
          anchorOrigin: { vertical: "top", horizontal: "right" },
        });
      }
    } catch (error) {
      enqueueSnackbar(error?.message || "Failed to save payroll preferences.", {
        variant: "error",
        anchorOrigin: { vertical: "top", horizontal: "right" },
      });
    }
  };

  return (
    <SnackbarProvider
      domRoot={typeof document !== "undefined" ? document.body : undefined}
      maxSnack={3}
      preventDuplicate
      anchorOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      autoHideDuration={4000}
    >
      {loading && <Loader loading={loading} />}

      <Stack direction="column" spacing={3} sx={{ width: "100%", p: 5 }}>
        {/* Payroll Hours Conversion Preference */}
        <Stack direction="row" alignItems="center" sx={{ gap: 5 }}>
          <Typography
            fontSize="0.7rem"
            textAlign="left"
            sx={{ width: 180, flexShrink: 0, fontWeight: 500 }}
          >
            Payroll Hours Conversion
          </Typography>
          <Box sx={{ flexShrink: 0 }}>
            <RadioGroup
              row
              name="IsPayrollHours"
              value={isPayrollHours ? "minutes" : "decimals"}
              onChange={(e) => setIsPayrollHours(e.target.value === "minutes")}
            >
              <FormControlLabel
                value="decimals"
                control={<Radio size="small" sx={{ p: 0.5, ml: 1, mr: 1 }} />}
                label={<Typography fontSize="0.7rem">Decimals</Typography>}
                sx={{ width: 150 }}
              />
              <FormControlLabel
                value="minutes"
                control={<Radio size="small" sx={{ p: 0.5, ml: 1, mr: 1 }} />}
                label={<Typography fontSize="0.7rem">Hours to minutes</Typography>}
              />
            </RadioGroup>
          </Box>
        </Stack>

        {/* Payroll Item Type Mapping */}
        <Stack direction="row" alignItems="center" sx={{ gap: 5 }}>
          <Typography
            fontSize="0.7rem"
            textAlign="left"
            sx={{ width: 180, flexShrink: 0, fontWeight: 500 }}
          >
            Payroll Item type Mapping
          </Typography>
          <Box
            onClick={() => setIsItemMappingOpen(true)}
            sx={{
              border: "1px solid #ccc",
              borderRadius: 1,
              p: 1,
              cursor: "pointer",
              "&:hover": {
                backgroundColor: "#f0f0f0",
              },
            }}
          >
            <Typography sx={{ fontSize: "0.7rem", color: "#00a2ff", fontWeight: 600 }}>
              View Mapped List ({itemMapCount} items)
            </Typography>
          </Box>
        </Stack>

        {/* Payroll Company Preference */}
        <Stack direction="row" alignItems="center" sx={{ gap: 5, width: "100%" }}>
          <Typography
            fontSize="0.7rem"
            textAlign="left"
            sx={{ width: 180, flexShrink: 0, fontWeight: 500 }}
          >
            Payroll Company Preference
          </Typography>
          <Box sx={{ flexShrink: 0 }}>
            <RadioGroup
              row
              name="CompanyPreference"
              value={companyPreference}
              onChange={(e) => {
                const newPreference = e.target.value;
                setCompanyPreference(newPreference);
                if (newPreference === PREFERENCE_MODES.ALL_CORP) {
                  setImportGroup1(AUTOMATION_TYPES.EMAIL);
                }
              }}
            >
              <FormControlLabel
                value={PREFERENCE_MODES.BY_CORP}
                control={<Radio size="small" sx={{ p: 0.5, ml: 1, mr: 1 }} />}
                label={<Typography fontSize="0.7rem">By corporation</Typography>}
                sx={{ width: 150 }}
              />
              <FormControlLabel
                value={PREFERENCE_MODES.ALL_CORP}
                control={<Radio size="small" sx={{ p: 0.5, ml: 1, mr: 1 }} />}
                label={<Typography fontSize="0.7rem">For all corporation</Typography>}
              />
            </RadioGroup>
          </Box>
          <Box sx={{ flexGrow: 1 }} />
          <Button
            size="small"
            variant="contained"
            aria-label="Save payroll preferences"
            onClick={handleSave}
            sx={{
              backgroundColor: "#1e98d7",
              color: "white",
              textTransform: "none",
              boxShadow: "none",
              fontSize: "0.7rem",
              px: 3,
              "&:hover": { backgroundColor: "#1e98d7", boxShadow: "none" },
            }}
          >
            Save
          </Button>
        </Stack>

        {/* Dynamic Content based on Preference */}
        {companyPreference === PREFERENCE_MODES.BY_CORP && (
          <IsByCorporationTable
            rows={corpRows}
            setRows={setCorpRows}
            onOpenApiModal={handleOpenApiModalForCorp}
            payrollVendorTypes={payrollVendorTypes}
          />
        )}

        {companyPreference === PREFERENCE_MODES.ALL_CORP && (
          <Stack direction="row" alignItems="center" sx={{ gap: 5 }}>
            <Typography
              fontSize="0.7rem"
              textAlign="left"
              sx={{ width: 180, flexShrink: 0, fontWeight: 500 }}
            >
              Import group preference
            </Typography>
            <Box sx={{ flexShrink: 0, display: "flex", gap: 2 }}>
              <Box sx={{ minWidth: 150 }}>
                <Dropdown
                  name="importGroup1"
                  value={AUTOMATION_OPTIONS.find((a) => a.id === importGroup1) || AUTOMATION_OPTIONS[0] || null}
                  options={AUTOMATION_OPTIONS}
                  getOptionLabel={(option) => option?.label || ""}
                  isEqual={(option, value) => String(option?.id) === String(value?.id)}
                  onChange={(name, newValue) => setImportGroup1(newValue?.id || "")}
                  sx={COMPACT_DROPDOWN_SX}
                />
              </Box>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Box sx={{ minWidth: 150 }}>
                  <Dropdown
                    name="importGroup2"
                    value={
                      Array.isArray(payrollVendorTypes)
                        ? payrollVendorTypes.find((v) => v.ID === importGroup2) || payrollVendorTypes[0] || null
                        : null
                    }
                    options={Array.isArray(payrollVendorTypes) ? payrollVendorTypes : []}
                    getOptionLabel={(option) => option?.Value || ""}
                    isEqual={(option, value) => String(option?.ID) === String(value?.ID)}
                    onChange={(name, newValue) => setImportGroup2(newValue?.ID || "")}
                    sx={COMPACT_DROPDOWN_SX}
                  />
                </Box>
                {importGroup1 === AUTOMATION_TYPES.API && (
                  <Link
                    component="button"
                    variant="body2"
                    onClick={handleOpenApiModalForAll}
                    sx={{ ml: 1.5, fontWeight: 600, fontSize: "0.7rem", textDecoration: "underline" }}
                  >
                    {isApiConfigured ? "Configured" : "SET"}
                  </Link>
                )}
              </Box>
            </Box>
          </Stack>
        )}

        {/* API Credentials Configuration Dialog */}
        <ApiConfigurationModal
          open={isApiModalOpen}
          onClose={() => setIsApiModalOpen(false)}
          initialData={apiModalData}
          onSave={handleApiModalSave}
        />

        {/* Alert Popup */}
        <PayrollsPopup
          open={alertPopup.open}
          popupType={alertPopup.type}
          message={alertPopup.message}
          onConfirm={alertPopup.onConfirm}
          onCancel={hidePopup}
        />

        {/* Item Mapping Drawer */}
        {isItemMappingOpen && (
          <PayrollItemTypeMapping
            open={isItemMappingOpen}
            onClose={() => setIsItemMappingOpen(false)}
            onUpdateAndNext={() => setIsItemMappingOpen(false)}
          />
        )}
      </Stack>
    </SnackbarProvider>
  );
};

export default PayrollPreference;